import React, { useState } from 'react'
import { useNavigate, generatePath } from 'react-router'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Button, Chip, CircularProgress, Grid, Typography } from '@mui/material'
import { ConfirmationAlert } from '@emerald-works-nova/components'
import { BannerStatic } from '../../../components'
import { NoThumbnailImage } from '../../../components/icons'
import { routes } from '../../../routes'
import { useStyles } from './style'

const getModuleStatus = (module) => {
  if (module.importJobDetails?.status === 'PROCESSING') return 'processing'
  if (module.importJobDetails?.status === 'ERROR') return 'error'
  if (module.status === 'draft') return 'draft'
  if (module.status === 'inactive') return 'inactive'
  if (module.status === 'active') return 'active'
  return 'unknown'
}

const STATUS_CHIP = {
  processing: { label: 'Processing', color: 'info' },
  error: { label: 'Error', color: 'error' },
  draft: { label: 'Draft', color: 'default' },
  inactive: { label: 'Inactive', color: 'warning' },
  active: { label: 'Active', color: 'success' },
  unknown: { label: 'Unknown', color: 'default' }
}

const ModuleItem = ({ module, index, onDeleted }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const status = getModuleStatus(module)
  const chip = STATUS_CHIP[status]

  const [deleteModule] = useEvent([
    {
      eventName: 'adminSkillsPracticeDelete',
      onSuccess: () => {
        BannerStatic.show({ label: 'Module deleted successfully.' })
        onDeleted()
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error deleting the module.', isError: true })
      }
    }
  ])

  const isDeleting = deleteModule.isWorking

  const getThumbnailImage = () => {
    if (module.image?.imgUrl) {
      return (
        <Box data-test={`module-thumbnail-${index}`}>
          <img
            alt={module.image?.altText}
            src={module.image.imgUrl}
            className={classes.cardImage}
          />
        </Box>
      )
    }
    return (
      <div className={classes.noImageIconWrapper}>
        <NoThumbnailImage />
      </div>
    )
  }

  const handleDelete = () => {
    deleteModule.trigger({ moduleId: module.pk })
    setShowDeleteConfirm(false)
  }

  const handleRetryUpload = () => {
    navigate(`${routes.skillsPracticeNew.path}?moduleId=${module.pk}`)
  }

  const handleCompleteSetup = () => {
    navigate(generatePath(routes.skillsPracticeEdit.path, { moduleId: module.pk }))
  }

  const handleEdit = () => {
    navigate(generatePath(routes.skillsPracticeEdit.path, { moduleId: module.pk }))
  }

  const renderActions = () => {
    if (status === 'processing') {
      return <CircularProgress size={20} />
    }

    return (
      <Box className={classes.actionsColumn}>
        {status === 'error' && (
          <Button size='small' variant='outlined' color='primary' onClick={handleRetryUpload} data-test={`module-retry-${index}`}>
            Retry Upload
          </Button>
        )}
        {(status === 'draft' || status === 'unknown') && (
          <Button size='small' variant='outlined' color='primary' onClick={handleCompleteSetup} data-test={`module-complete-setup-${index}`}>
            Complete Setup
          </Button>
        )}
        {(status === 'inactive' || status === 'active') && (
          <Button size='small' variant='outlined' color='primary' onClick={handleEdit} data-test={`module-edit-${index}`}>
            Edit
          </Button>
        )}
        <Button size='small' variant='outlined' color='error' onClick={() => setShowDeleteConfirm(true)} disabled={isDeleting} data-test={`module-delete-${index}`}>
          {isDeleting ? 'Deleting…' : 'Delete'}
        </Button>
      </Box>
    )
  }

  return (
    <>
      <Grid
        role='row'
        data-test={`skills-practice-module-item-${index}`}
        container
        tabIndex={0}
        direction='row'
        alignItems='center'
        className={classes.gridRow}
      >
        <Grid item md={5} className={classes.titleColumn}>
          <Box display='flex' alignItems='center'>
            {getThumbnailImage()}
            <Box>
              <Typography variant='body2' data-test={`module-title-${index}`}>
                {module.title || '(untitled)'}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item md={2}>
          <Chip
            label={chip.label}
            color={chip.color}
            size='small'
            sx={{ borderRadius: '4px', color: chip.color === 'default' ? undefined : '#FFF' }}
          />
        </Grid>
        <Grid item md={2}>
          <Typography variant='body2'>
            {module.updatedAt ? new Date(module.updatedAt).toLocaleDateString() : '—'}
          </Typography>
        </Grid>
        <Grid item md={3}>
          {renderActions()}
        </Grid>
      </Grid>
      <ConfirmationAlert
        title='Delete this module?'
        description='This action cannot be undone. The module will be permanently removed from the catalog.'
        agreeText='Delete'
        showModal={showDeleteConfirm}
        onConfirmation={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  )
}

export default ModuleItem
